import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// redux slice
import { logOut, selectUserDetails, selectSelectedOrganization, selectOrganizationByInternalUser } from "../../features/auth/authSlice";
import { useLogoutMutation } from "../../features/auth/logoutApiSlice";
import { selectImpersonatedUser } from "../../features/admin/impersonated-user/impersonatedUserSlice";

// local storage utils
import { storageDelete } from "../../utils/localStorage";
import { markExplicitLogout, HOME_FROM_LOCATION } from "../../utils/logout-flag";

// mui components
import {
	Box,
	Divider,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	Collapse,
} from "@mui/material";

// custom components
import Heading5 from "../typography/heading-05";

// custom modal
import HelpCenterModal from "../modal/help-center-modal";

// mui icons
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import {
	HelpOutlineOutlined,
	ExpandLess,
	ExpandMore,
} from "@mui/icons-material";

// custom icons
import DashboardLogo from "../../assets/img/dashboard-logo.png";

// drawer data
import { drawerTopData, drawerBottomData } from "./drawer-data";

// helper utils
import {
	selectedUserWithLoanChannel,
	selectedUserWithAccessLevel,
} from "../../utils/common-helpers";
import {
	LOAN_CHANNEL_CONSTANTS,
	TPO_SECONDARY,
	BROKER_ADMIN,
	TPO_SECONDARY_USER_TITLE,
} from "../../utils/common-constants";

// custom styles
import styles from "./index.module.scss";

const CustomDrawer = ({
	drawerWidth,
	handleDrawerItemClick,
	mobileOpen,
	handleDrawerToggle,
}) => {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [currentPath, setCurrentPath] = useState("");
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const organizationByInternalUser = useSelector(selectOrganizationByInternalUser);
	const [helpCenterOpened, setHelpCenterOpened] = useState(false);

	const [expandedItems, setExpandedItems] = useState({});
	const [logoutApi] = useLogoutMutation();

	useEffect(() => {
		let location = pathname.split("/");
		console.log("92 pathname", location[1]);
		setCurrentPath(location[1]);
	}, [pathname]);

	const isCurrentPath = (loanUrl) => {
		if (typeof loanUrl === "string") {
			return currentPath === loanUrl?.toLowerCase();
		} else {
			return loanUrl.some((url) => pathname.includes(url));
		}
	};

	const handleLogout = async () => {
		try {
			await logoutApi().unwrap();
		} catch (_) {
			// proceed with local logout even if API call fails
		}
		// set before dispatch: auth/logOut resets the whole Redux tree, so this
		// can't live in Redux state. Guards App.js's catch-all route in case it
		// renders once more with the pre-logout URL before navigate() below
		// takes effect, which would otherwise re-stash this page as `from`.
		markExplicitLogout();
		dispatch(logOut());
		storageDelete("access-token");
		storageDelete("refresh-token");
		navigate("/login", {
			replace: true,
			state: { from: HOME_FROM_LOCATION },
		});
	};

	const handleExpandClick = (itemTitle) => {
		setExpandedItems((prev) => ({
			...prev,
			[itemTitle]: !prev[itemTitle],
		}));
	};

	/* Quick-pricer External User */
	const isItemDisabled = (item) => {
		if (!userDetails) return true;

		const effectiveChannel = selectedUserWithLoanChannel(
			userDetails,
			impersonatedUser,
			selectedOrganization
		)?.loan_channel;

		const isWholesaleOrCB =
			effectiveChannel === LOAN_CHANNEL_CONSTANTS.WHS ||
			effectiveChannel === LOAN_CHANNEL_CONSTANTS.CB;

		if (userDetails?.snowFlakeOrganizations?.length > 0) {
			const enabledForInternalUsers = userDetails?.snowFlakeOrganizations?.find(
				(org) => org.company_name === organizationByInternalUser
			);

			if (item?.title === "Quick Pricer") {
				console.log("enabledForInternalUsers", enabledForInternalUsers);
				return !enabledForInternalUsers?.allow_quick_pricer;
			}
		} else if (userDetails?.organizations?.length > 0) {
			if (item?.title === "Quick Pricer") {
				console.log(
					"effectiveChannel Organization",
					userDetails?.organizations
				);

				const isCorrOrNdc =
					effectiveChannel === LOAN_CHANNEL_CONSTANTS.CORR ||
					effectiveChannel === LOAN_CHANNEL_CONSTANTS.NDC;
				const effectiveUserForAccess = selectedUserWithAccessLevel(
					userDetails,
					impersonatedUser
				);
				const accessLevel = effectiveUserForAccess?.access_level;
				const userTitle = effectiveUserForAccess?.user_title;
				const isTpoSecondaryOrBrokerAdmin =
					accessLevel === TPO_SECONDARY ||
					accessLevel === BROKER_ADMIN ||
					userTitle?.includes(TPO_SECONDARY_USER_TITLE);

				return !(isWholesaleOrCB || (isCorrOrNdc && isTpoSecondaryOrBrokerAdmin));
			}
		} else {
			if (item?.title === "Quick Pricer") {
				console.log(
					"No organization data available for userDetails",
					userDetails
				);
				return true;
			}
		}

		return false;
	};

	const shouldRenderItem = (item) => {
		if (!userDetails) return false;

		const isExecutiveOrManager =
			userDetails?.user_roles?.includes("executive") ||
			userDetails?.user_roles?.includes("manager") ||
			userDetails?.user_roles?.includes("AE") ||
			userDetails?.user_roles?.includes("AM") ||
			userDetails?.user_roles?.includes("PA") ||
			userDetails?.user_roles?.includes("assistant");

		const isTpoSecondary = userDetails?.access_level?.includes("TPO Secondary");

		const effectiveChannel = selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)?.loan_channel;

		const isWholesaleOrCB =
			effectiveChannel === LOAN_CHANNEL_CONSTANTS.WHS ||
			effectiveChannel === LOAN_CHANNEL_CONSTANTS.CB;

		if (item?.url === "new-loan") {
			// does not render for executive or manager
			return !(isExecutiveOrManager);
		}

		if (item?.url === "upload-history") {
			// follows same rules as new-loan
			return !(isExecutiveOrManager || isTpoSecondary);
		}

		if (item?.title === "Order Appraisal") {
			// does not render for either NDC or CORR users
			return !(
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
			);
		}

		if (item?.title === "Schedule Training") {
			// does not render for executive or manager
			return !isExecutiveOrManager;
		}

		if (item?.title === "Price my Loan") {
			// does not render for either NDC or CORR users
			return !(
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
			);
		}

		return true;
	};

	const drawer = (
		<Box className={styles.drawerDiv}>
			<Box className={styles.logoContainer}>
				<img src={DashboardLogo} alt="dashboard logo" />
			</Box>

			<List className={styles.drawerList}>
				{drawerTopData?.map((item, index) => {
					if (shouldRenderItem(item)) {
						const hasSubItems = item.subItems && item.subItems.length > 0;
						const isExpanded = expandedItems[item.title];
						const isSelected = isCurrentPath(item.url);

						return (
							<React.Fragment key={index}>
								<ListItem
									disablePadding
									className={
										isItemDisabled(item)
											? styles.drawerDisabledItem
											: isSelected
												? styles.drawerSelectedItem
												: styles.drawerItem
									}
								>
									<ListItemButton
										onClick={() => {
											if (!isItemDisabled(item)) {
												handleDrawerItemClick(item.title, item.path);
											}
										}}
									>
										<ListItemIcon>{item.icon}</ListItemIcon>
										<Heading5
											text={item.title}
											fontType="semibold"
											extraClass={
												isSelected
													? styles.drawerSelectedText
													: styles.drawerText
											}
										/>
										{hasSubItems && (
											<Box
												sx={{ marginLeft: "auto", cursor: "pointer" }}
												onClick={(e) => {
													e.stopPropagation();
													handleExpandClick(item.title);
												}}
											>
												{isExpanded ? <ExpandLess /> : <ExpandMore />}
											</Box>
										)}
									</ListItemButton>
								</ListItem>
								{hasSubItems && (
									<Collapse in={isExpanded} timeout="auto" unmountOnExit>
										<List component="div" disablePadding>
											{item.subItems.map((subItem, subIndex) => (
												<ListItem
													key={`${index}-${subIndex}`}
													disablePadding
													className={
														isCurrentPath(subItem.url)
															? styles.drawerSelectedItem
															: styles.drawerItem
													}
													sx={{ pl: 4 }}
												>
													<ListItemButton
														onClick={() =>
															handleDrawerItemClick(subItem.title, subItem.path)
														}
													>
														<ListItemIcon sx={{ minWidth: "32px" }}>
															{subItem.icon}
														</ListItemIcon>
														<Heading5
															text={subItem.title}
															fontType="semibold"
															extraClass={
																isCurrentPath(subItem.url)
																	? styles.drawerSelectedText
																	: styles.drawerText
															}
														/>
													</ListItemButton>
												</ListItem>
											))}
										</List>
									</Collapse>
								)}
							</React.Fragment>
						);
					}
					return null;
				})}
			</List>
			{userDetails && <Divider className={styles.dividerLine} />}
			<List className={`${styles.logoutContainer} ${styles.drawerList}`}>
				{userDetails && (
					<ListItem disablePadding className={styles.drawerItem}>
						<ListItemButton onClick={handleLogout}>
							<ListItemIcon>
								<LogoutOutlinedIcon sx={{ fontSize: "32px" }} />
							</ListItemIcon>
							<Heading5
								text="Log Out"
								fontType="semibold"
								extraClass={styles.drawerText}
							/>
						</ListItemButton>
					</ListItem>
				)}
				{userDetails?.user_roles?.includes("admin") &&
					userDetails &&
					drawerBottomData?.map((item, index) => (
						<ListItem
							key={index}
							disablePadding
							className={
								isCurrentPath(item.url)
									? styles.drawerSelectedItem
									: styles.drawerItem
							}
						>
							<ListItemButton
								onClick={() => handleDrawerItemClick(item.title, item.path)}
							>
								<ListItemIcon>{item.icon}</ListItemIcon>
								<Heading5
									text={item.title}
									fontType="semibold"
									extraClass={
										isCurrentPath(item.url)
											? styles.drawerSelectedText
											: styles.drawerText
									}
								/>
							</ListItemButton>
						</ListItem>
					))}
			</List>
			<List className={`${styles.helpContainer} ${styles.drawerList}`}>
				<List disablePadding className={styles.drawerItem}>
					<ListItemButton onClick={() => setHelpCenterOpened(true)}>
						<ListItemIcon>
							<HelpOutlineOutlined />
						</ListItemIcon>
						<Heading5
							text="Help Center"
							fontType="semibold"
							extraClass={styles.drawerText}
						/>
					</ListItemButton>
				</List>
			</List>
		</Box>
	);

	return (
		<Box
			component="nav"
			sx={{
				width: { sm: drawerWidth },
				flexShrink: { sm: 0 },
			}}
			aria-label="mailbox folders"
		>
			<Drawer
				variant="temporary"
				open={mobileOpen}
				onClose={handleDrawerToggle}
				ModalProps={{
					keepMounted: true, // Better open performance on mobile.
				}}
				sx={{
					display: { xs: "block", sm: "none" },
					"& .MuiDrawer-paper": {
						boxSizing: "border-box",
						width: drawerWidth,
					},
				}}
				className={styles.drawerMobileContainer}
			>
				{drawer}
			</Drawer>
			<Drawer
				variant="permanent"
				sx={{
					display: { xs: "none", sm: "block" },
					"& .MuiDrawer-paper": {
						boxSizing: "border-box",
						width: drawerWidth,
					},
				}}
				open
				className={styles.drawerContainer}
			>
				{drawer}
			</Drawer>
			<HelpCenterModal
				open={helpCenterOpened}
				showHelpCentre={setHelpCenterOpened}
			/>
		</Box>
	);
};

export default CustomDrawer;
